import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, ExternalLink, Tag, BookOpen } from 'lucide-react';
import { EmptyState } from './EmptyState';
import { Tool, PricingModel, Employee, AppSettings, Curriculum, DEFAULT_CRITICALITY_LEVELS } from '../types';

const TOOL_CURRENCY_OPTIONS = [
  { code: 'EUR', label: 'Euro (€)' }, { code: 'USD', label: 'USD ($)' }, { code: 'GBP', label: 'GBP (£)' },
  { code: 'CHF', label: 'CHF (Fr.)' }, { code: 'JPY', label: 'JPY (¥)' }, { code: 'CAD', label: 'CAD (CA$)' },
  { code: 'AUD', label: 'AUD (A$)' }, { code: 'CNY', label: 'CNY / RMB (¥)' },
];

interface ToolModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (tool: Tool) => Promise<void> | void;
  initialData?: Tool;
  employees: Employee[];
  curricula?: Curriculum[];
  settings: AppSettings;
}

export const ToolModal: React.FC<ToolModalProps> = ({ isOpen, onClose, onSave, initialData, employees, curricula = [], settings }) => {
  const [formData, setFormData] = useState<Partial<Tool>>({
    category: (settings.categories && settings.categories[0]) || 'Other',
    importance: (settings.criticalityLevels ?? DEFAULT_CRITICALITY_LEVELS).find(l => l.sortOrder === 1)?.id ?? (settings.criticalityLevels ?? DEFAULT_CRITICALITY_LEVELS)[0]?.id ?? 'Supporting',
    pricingModel: 'PerUser',
    complexity: 1,
    teamLiteracy: 0,
    satisfactionScore: 0,
    userCount: 1,
    basePrice: 0,
    monthlyCost: 0,
    assignedUserIds: [],
    tags: [],
    leadTimeMonths: 1, // Default to 1 month
    currency: settings.currency || 'EUR',
  });
  
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        tags: initialData.tags || [],
        leadTimeMonths: initialData.leadTimeMonths ?? 1,
        currency: initialData.currency ?? settings.currency ?? 'EUR',
      });
    } else {
      setFormData({
        name: '',
        category: (settings.categories && settings.categories[0]) || 'Other',
        importance: (settings.criticalityLevels ?? DEFAULT_CRITICALITY_LEVELS).find(l => l.sortOrder === 1)?.id ?? (settings.criticalityLevels ?? DEFAULT_CRITICALITY_LEVELS)[0]?.id ?? 'Supporting',
        pricingModel: 'PerUser',
        userCount: 1,
        basePrice: 0,
        monthlyCost: 0,
        complexity: 2,
        teamLiteracy: 0,
        renewalDate: new Date().toISOString().split('T')[0],
        noticeDeadline: new Date().toISOString().split('T')[0],
        leadTimeMonths: 1,
        satisfactionScore: 0,
        assignedUserIds: [],
        tags: [],
        teams: [],
        currency: settings.currency ?? 'EUR',
      });
    }
  }, [initialData, isOpen, settings.categories?.length, settings.currency]);

  // Recalculate monthly cost whenever dependent fields change
  useEffect(() => {
    let cost = 0;
    const price = Number(formData.basePrice) || 0;
    const users = Number(formData.userCount) || 0;

    if (formData.pricingModel === 'PerUser') {
        cost = price * users;
    } else {
        cost = price; // Fixed or Variable treated as flat input for now
    }
    setFormData(prev => ({ ...prev, monthlyCost: cost }));
  }, [formData.pricingModel, formData.basePrice, formData.userCount]);

  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && tagInput.trim()) {
          e.preventDefault();
          if (!formData.tags?.includes(tagInput.trim())) {
              setFormData(prev => ({
                  ...prev,
                  tags: [...(prev.tags || []), tagInput.trim()]
              }));
          }
          setTagInput('');
      }
  };

  const addExistingTag = (tag: string) => {
      if (!formData.tags?.includes(tag)) {
          setFormData(prev => ({
              ...prev,
              tags: [...(prev.tags || []), tag]
          }));
      }
  };

  const removeTag = (tagToRemove: string) => {
      setFormData(prev => ({
          ...prev,
          tags: prev.tags?.filter(t => t !== tagToRemove)
      }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || saving) return;

    const tool: Tool = {
      id: formData.id || crypto.randomUUID(),
      name: formData.name,
      category: formData.category || 'Other',
      importance: formData.importance || 'Supporting',
      pricingModel: formData.pricingModel as PricingModel,
      basePrice: Number(formData.basePrice),
      userCount: Number(formData.userCount),
      monthlyCost: Number(formData.monthlyCost),
      complexity: Number(formData.complexity),
      teamLiteracy: Number(formData.teamLiteracy),
      renewalDate: formData.renewalDate || new Date().toISOString().split('T')[0],
      noticeDeadline: formData.noticeDeadline || formData.renewalDate || new Date().toISOString().split('T')[0],
      leadTimeMonths: Number(formData.leadTimeMonths) || 0,
      satisfactionScore: Number(formData.satisfactionScore),
      description: formData.description,
      website: formData.website,
      notes: formData.notes,
      teams: formData.teams || [],
      powerUserId: formData.powerUserId,
      assignedUserIds: formData.assignedUserIds || [],
      trainingUrl: formData.trainingUrl,
      tags: formData.tags || [],
      currency: formData.currency ?? settings.currency ?? undefined,
      feedback: formData.feedback ?? [],
      vendorContactName: formData.vendorContactName,
      vendorContactEmail: formData.vendorContactEmail,
      vendorSupportUrl: formData.vendorSupportUrl,
      vendorPortalUrl: formData.vendorPortalUrl,
      contractNumber: formData.contractNumber,
      accountManager: formData.accountManager,
    };

    setSaving(true);
    try {
      await onSave(tool);
      onClose();
    } catch (err) {
      console.error('Failed to save tool:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b border-slate-100 shrink-0">
          <h2 className="text-xl font-bold text-slate-800">
            {initialData ? 'Edit Tool' : 'Add New Tool'}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              form="tool-modal-form"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100" title="Close">
              <X size={24} />
            </button>
          </div>
        </div>

        <form id="tool-modal-form" onSubmit={handleSubmit} className="p-6 space-y-8 overflow-y-auto">
          
          {/* Section 1: Core Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-indigo-600 uppercase tracking-wider flex items-center gap-2">
                1. General Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label htmlFor="tool-name" className="block text-sm font-medium text-slate-700 mb-1">Tool Name *</label>
                    <input
                    id="tool-name"
                    type="text"
                    required
                    value={formData.name || ''}
                    onChange={e => { const v = e.target.value; setFormData(prev => ({...prev, name: v})); }}
                    className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="e.g. Salesforce"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Website</label>
                    <input
                    type="text"
                    value={formData.website || ''}
                    onChange={e => { const v = e.target.value; setFormData(prev => ({...prev, website: v})); }}
                    className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="e.g. salesforce.com"
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                    <textarea
                    value={formData.description || ''}
                    onChange={e => { const v = e.target.value; setFormData(prev => ({...prev, description: v})); }}
                    className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
                    rows={2}
                    placeholder="Brief description of what this tool does and why it's used"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                    <select
                    value={formData.category}
                    onChange={e => { const v = e.target.value; setFormData(prev => ({...prev, category: v})); }}
                    className="w-full rounded-lg border-slate-300 border p-2.5 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                    >
                    {(settings.categories || []).map(c => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Business Importance</label>
                    <select
                    data-testid="tool-importance-select"
                    value={formData.importance}
                    onChange={e => { const v = e.target.value; setFormData(prev => ({...prev, importance: v})); }}
                    className="w-full rounded-lg border-slate-300 border p-2.5 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                    >
                    {[...(settings.criticalityLevels ?? DEFAULT_CRITICALITY_LEVELS)].sort((a, b) => a.sortOrder - b.sortOrder).map(level => (
                        <option key={level.id} value={level.id}>{level.label}</option>
                    ))}
                    </select>
                </div>
                <div className="col-span-1 md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Teams</label>
                    <div className="flex flex-wrap gap-2 p-2 rounded-lg border border-slate-300 bg-white min-h-[44px]">
                        {settings.teams?.map(team => (
                            <label key={team} className="inline-flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.teams?.includes(team) ?? false}
                                    onChange={e => {
                                        const checked = e.target.checked;
                                        setFormData(prev => {
                                            const current = prev.teams || [];
                                            const next = checked ? [...current, team] : current.filter(t => t !== team);
                                            return { ...prev, teams: next };
                                        });
                                    }}
                                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span className="text-sm text-slate-700">{team}</span>
                            </label>
                        ))}
                        {(!settings.teams?.length) && (
                            <p className="text-sm text-slate-400 py-2">No teams configured. Create teams in Configuration first, then assign them to this tool.</p>
                        )}
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Responsible</label>
                    <select
                        value={formData.powerUserId || ''}
                        onChange={e => { const v = e.target.value; setFormData(prev => ({...prev, powerUserId: v})); }}
                        className="w-full rounded-lg border-slate-300 border p-2.5 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                    >
                        <option value="">-- Select an Owner --</option>
                        {employees.map(emp => (
                            <option key={emp.id} value={emp.id}>{emp.name} ({emp.role})</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1">
                        Training URL
                        <ExternalLink size={14} className="text-slate-400" />
                    </label>
                    <input
                    type="url"
                    value={formData.trainingUrl || ''}
                    onChange={e => { const v = e.target.value; setFormData(prev => ({...prev, trainingUrl: v})); }}
                    className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="https://company-lms.com/course/123"
                    />
                </div>
                
                <div className="col-span-1 md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1">
                        Tags
                        <Tag size={14} className="text-slate-400" />
                    </label>
                    <div className="flex flex-wrap gap-2 p-2 rounded-lg border border-slate-300 bg-white min-h-[44px]">
                        {formData.tags?.map(tag => (
                            <span key={tag} className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md text-xs font-bold flex items-center gap-1">
                                {tag}
                                <button type="button" onClick={() => removeTag(tag)} className="text-indigo-400 hover:text-indigo-900"><X size={12} /></button>
                            </span>
                        ))}
                        <input
                            type="text"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={handleAddTag}
                            className="flex-1 outline-none text-sm bg-transparent min-w-[80px]"
                            placeholder="Add tag..."
                        />
                    </div>
                    {/* Suggested Tags from Settings */}
                    {settings.tags.filter(t => !formData.tags?.includes(t)).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                        {settings.tags
                            .filter(t => !formData.tags?.includes(t))
                            .map(tag => (
                                <button 
                                    key={tag}
                                    type="button"
                                    onClick={() => addExistingTag(tag)}
                                    className="text-xs bg-slate-50 text-slate-500 px-2 py-0.5 rounded hover:bg-slate-100 transition-colors border border-slate-100"
                                >
                                    + {tag}
                                </button>
                            ))}
                    </div>
                    )}
                </div>
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Section 2: Pricing & Usage */}
          <div className="space-y-4">
             <h3 className="text-sm font-semibold text-indigo-600 uppercase tracking-wider flex items-center gap-2">
                2. Pricing & Usage
            </h3>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Pricing Model</label>
                    <select
                        value={formData.pricingModel}
                        onChange={e => { const v = e.target.value as PricingModel; setFormData(prev => ({...prev, pricingModel: v})); }}
                        className="w-full rounded-lg border-slate-300 border p-2.5 bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                    >
                        <option value="PerUser">Per User / License</option>
                        <option value="Fixed">Fixed Monthly Fee</option>
                        <option value="Variable">Variable / Usage Based</option>
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Devise de l&apos;outil</label>
                    <select
                        value={formData.currency ?? settings.currency ?? 'EUR'}
                        onChange={e => { const val = e.target.value; setFormData(prev => ({ ...prev, currency: val })); }}
                        className="w-full rounded-lg border-slate-300 border p-2.5 bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                    >
                        {TOOL_CURRENCY_OPTIONS.map(c => (
                            <option key={c.code} value={c.code}>{c.label}</option>
                        ))}
                    </select>
                </div>

                {formData.pricingModel === 'PerUser' && (
                    <>
                         <div>
                            <label htmlFor="cost-per-user" className="block text-xs font-bold text-slate-700 mb-1">Cost Per User ({settings.currencySymbol})</label>
                            <input
                                id="cost-per-user"
                                type="number"
                                min="0"
                                value={formData.basePrice}
                                onChange={e => { const v = Number(e.target.value); setFormData(prev => ({...prev, basePrice: v})); }}
                                className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-700 mb-1">Number of Users</label>
                            <input
                                type="number"
                                min="1"
                                value={formData.userCount}
                                onChange={e => { const v = Number(e.target.value); setFormData(prev => ({...prev, userCount: v})); }}
                                className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                            />
                        </div>
                    </>
                )}

                {(formData.pricingModel === 'Fixed' || formData.pricingModel === 'Variable') && (
                     <>
                        <div className="col-span-1">
                            <label htmlFor="monthly-cost" className="block text-xs font-bold text-slate-700 mb-1">
                                {formData.pricingModel === 'Variable' ? `Avg. Monthly Cost (${settings.currencySymbol})` : `Monthly Cost (${settings.currencySymbol})`}
                            </label>
                            <input
                                id="monthly-cost"
                                type="number"
                                min="0"
                                value={formData.basePrice}
                                onChange={e => { const v = Number(e.target.value); setFormData(prev => ({...prev, basePrice: v})); }}
                                className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                            />
                        </div>
                        <div className="col-span-1">
                            <label className="block text-xs font-bold text-slate-700 mb-1">Active Users</label>
                            <input
                                type="number"
                                min="1"
                                value={formData.userCount}
                                onChange={e => { const v = Number(e.target.value); setFormData(prev => ({...prev, userCount: v})); }}
                                className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                            />
                        </div>
                    </>
                )}
            </div>
            
            <div className="flex justify-between items-center bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                <span className="text-sm font-medium text-indigo-900">Total Monthly Expenditure:</span>
                <span className="text-xl font-bold text-indigo-700">{settings.currencySymbol}{formData.monthlyCost?.toLocaleString()}</span>
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Section 3: Governance & Audit */}
          <div className="space-y-4">
             <h3 className="text-sm font-semibold text-indigo-600 uppercase tracking-wider flex items-center gap-2">
                3. Governance & Audit
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                    <label htmlFor="contract-renewal-date" className="block text-sm font-medium text-slate-700 mb-1">Contract Renewal Date</label>
                    <input
                        id="contract-renewal-date"
                        type="date"
                        value={formData.renewalDate}
                        onChange={e => { const v = e.target.value; setFormData(prev => ({...prev, renewalDate: v})); }}
                        className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                </div>
                <div>
                    <label htmlFor="notice-deadline" className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1">
                        Notice Deadline
                        <span title="Last date to cancel before auto-renewal">
                            <AlertCircle size={14} className="text-slate-400" />
                        </span>
                    </label>
                    <input
                        id="notice-deadline"
                        type="date"
                        value={formData.noticeDeadline}
                        onChange={e => { const v = e.target.value; setFormData(prev => ({...prev, noticeDeadline: v})); }}
                        className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-amber-500 outline-none"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Lead Time (Months)</label>
                    <input
                        type="number"
                        min="0"
                        value={formData.leadTimeMonths}
                        onChange={e => { const v = Number(e.target.value); setFormData(prev => ({...prev, leadTimeMonths: v})); }}
                        className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none"
                        placeholder="e.g. 1"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">Review starts X months before deadline</p>
                </div>
            </div>
          </div>

          {/* Section 4: Scores (read-only averages from feedback / training) */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-indigo-600 uppercase tracking-wider flex items-center gap-2">
                4. Scores
            </h3>
            <p className="text-xs text-slate-500">Averages from user satisfaction and proficiency (feedback and training completion).</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-50 rounded-lg border border-slate-100 p-4">
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Average Satisfaction</label>
                    <div className="text-2xl font-bold text-slate-800">
                        {(formData.satisfactionScore != null && formData.satisfactionScore > 0)
                          ? `${(formData.satisfactionScore / 10).toFixed(1)}/10`
                          : '—'}
                    </div>
                </div>
                <div className="bg-slate-50 rounded-lg border border-slate-100 p-4">
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Average Proficiency</label>
                    <div className="text-2xl font-bold text-slate-800">
                        {(formData.teamLiteracy != null && formData.teamLiteracy > 0)
                          ? `${Number(formData.teamLiteracy).toFixed(1)}/10`
                          : '—'}
                    </div>
                </div>
            </div>
          </div>

          {/* Section 5: Training (curricula linked to this tool) */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-indigo-600 uppercase tracking-wider flex items-center gap-2">
              <BookOpen size={16} />
              5. Training (Curriculum)
            </h3>
            <p className="text-xs text-slate-500">Training modules linked to this tool. Add or edit them in the Training section.</p>
            {formData.id ? (
              curricula.filter(c => c.toolId === formData.id).length === 0 ? (
                <EmptyState
                  icon={<BookOpen size={40} />}
                  title="No linked training"
                  subtitle="Create curriculum entries in the Training section and link them to this tool."
                />
              ) : (
                <ul className="space-y-2">
                  {curricula.filter(c => c.toolId === formData.id).map(cur => (
                    <li key={cur.id} className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg border border-slate-100 p-3">
                      <div className="min-w-0">
                        <span className="font-medium text-slate-800">{cur.name}</span>
                        {cur.description && <p className="text-xs text-slate-500 truncate mt-0.5">{cur.description}</p>}
                      </div>
                      {cur.url && (
                        <a href={cur.url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-indigo-600 hover:text-indigo-700 flex items-center gap-1 text-sm">
                          <ExternalLink size={14} />
                          Open
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )
            ) : (
              <div className="bg-amber-50 rounded-lg border border-amber-200 p-4 text-sm text-amber-800">
                Save the tool first, then add training from the <strong>Training</strong> section.
              </div>
            )}
          </div>

          <hr className="border-slate-100" />

          {/* Section 6: Vendor Contact */}
          <details className="group">
            <summary className="text-sm font-semibold text-indigo-600 uppercase tracking-wider flex items-center gap-2 cursor-pointer list-none select-none">
              6. Vendor Contact
              <span className="text-xs text-slate-400 font-normal normal-case">({formData.vendorContactName || formData.accountManager ? 'configured' : 'optional'})</span>
            </summary>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Account Manager</label>
                <input type="text" value={formData.accountManager || ''} onChange={e => setFormData(prev => ({...prev, accountManager: e.target.value}))} className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="John Smith" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Contact Email</label>
                <div className="flex gap-2">
                  <input type="email" value={formData.vendorContactEmail || ''} onChange={e => setFormData(prev => ({...prev, vendorContactEmail: e.target.value}))} className="flex-1 rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="sales@vendor.com" />
                  {formData.vendorContactEmail && (
                    <a href={`mailto:${formData.vendorContactEmail}`} className="px-3 py-2.5 bg-slate-100 rounded-lg hover:bg-slate-200 text-slate-600" title="Send email"><ExternalLink size={16} /></a>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name</label>
                <input type="text" value={formData.vendorContactName || ''} onChange={e => setFormData(prev => ({...prev, vendorContactName: e.target.value}))} className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Vendor Representative" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Contract Number</label>
                <input type="text" value={formData.contractNumber || ''} onChange={e => setFormData(prev => ({...prev, contractNumber: e.target.value}))} className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="CTR-2026-001" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Support URL</label>
                <div className="flex gap-2">
                  <input type="url" value={formData.vendorSupportUrl || ''} onChange={e => setFormData(prev => ({...prev, vendorSupportUrl: e.target.value}))} className="flex-1 rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="https://vendor.com/support" />
                  {formData.vendorSupportUrl && (
                    <a href={formData.vendorSupportUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-2.5 bg-slate-100 rounded-lg hover:bg-slate-200 text-slate-600" title="Open"><ExternalLink size={16} /></a>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Portal URL</label>
                <div className="flex gap-2">
                  <input type="url" value={formData.vendorPortalUrl || ''} onChange={e => setFormData(prev => ({...prev, vendorPortalUrl: e.target.value}))} className="flex-1 rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="https://vendor.com/dashboard" />
                  {formData.vendorPortalUrl && (
                    <a href={formData.vendorPortalUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-2.5 bg-slate-100 rounded-lg hover:bg-slate-200 text-slate-600" title="Open"><ExternalLink size={16} /></a>
                  )}
                </div>
              </div>
            </div>
          </details>
        </form>
      </div>
    </div>
  );
};